import { yupResolver } from '@hookform/resolvers/yup'
import format from 'date-fns/format'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import s from './Settings.module.scss'
import styles from 'App.module.scss'

import { Subscription } from './components/Subscription'
import { SubscriptionHistory } from './components/SubscriptionHistory/SubscriptionHistory'

import transformUTCToLocale from 'utils/transformUTCToLocale'

import { useFetch } from 'hooks/useFetch'
import { useModal } from 'hooks/useModal'

import { useAppErrorContext } from 'contexts/AppErrorContext'
import { useAppSuccessMessageContext } from 'contexts/SuccessMessageContext'

import { settingsApi } from 'api/settings-api'

import { BriefcaseIcon } from 'assets/icons/BriefcaseIcon'
import { ClockIcon } from 'assets/icons/ClockIcon'
import { CopyIcon } from 'assets/icons/CopyIcon'
import { DiamondIcon } from 'assets/icons/DiamondIcon'
import { InfoIcon } from 'assets/icons/InfoIcon'
import { LockIcon } from 'assets/icons/LockIcon'
import { PublisherIcon } from 'assets/icons/PublisherIcon'
import { TagIcon } from 'assets/icons/TagIcon'

import { CustomDropzone } from 'components/Controls/CustomDropzone/CustomDropzone'
import { Input } from 'components/Controls/Input/Input'
import { InputControlled } from 'components/Controls/Input/InputControlled'
import { SelectControlled } from 'components/Controls/Select/SelectControlled'
import { TextareaControlled } from 'components/Controls/Textarea/TextareaControlled'
import { CustomButton } from 'components/UI/CustomButton/CustomButton'
import Header from 'components/UI/Header'
import PageTitle from 'components/UI/PageTitle'
import { Spinner } from 'components/UI/Spinner/Spinner'

import { SETTINGS_SCHEMA } from 'pages/Settings/settingValidation'

const ACCTYPE_NAME_BY_ACCTYPE_VALUE = ['Ad Network'] as const
const MODTYPE_NAME_BY_MODTYPE_VALUE = ['Auto-approval', 'Manual'] as const

const optionsAccountType = [{ value: 0, label: 'Ad Network' }]

const optionsAutoApproval = [
  { value: 0, label: 'Auto-approval' },
  { value: 1, label: 'Manual' },
]

interface IFormData {
  name: string
  description: string
  accountType: { value: number; label: string }
  moderationType: { value: number; label: string }
  avatar: File[]
}

const Settings = () => {
  const { setAppError } = useAppErrorContext()
  const { setSuccessMessage } = useAppSuccessMessageContext()

  const { show: showSub, handleClose: handleCloseSub, handleOpen: handleOpenSub } = useModal()
  const { show: showSubHistory, handleClose: handleCloseSubHustory, handleOpen: handleOpenSubHistory } = useModal()

  const {
    data: settings,
    loading,
    fetchData: fetchSettings,
  } = useFetch({
    getData: () => settingsApi.get(),
  })

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<IFormData>({
    mode: 'onBlur',
    resolver: yupResolver(SETTINGS_SCHEMA),
    defaultValues: {
      name: '',
      description: '',
      accountType: { value: 0, label: ACCTYPE_NAME_BY_ACCTYPE_VALUE[0] },
      moderationType: { value: 0, label: MODTYPE_NAME_BY_MODTYPE_VALUE[0] },
      avatar: [],
    },
  })

  useEffect(() => {
    if (settings) {
      reset({
        name: settings?.user?.name || '',
        description: settings?.user?.description || '',
        accountType: {
          value: settings?.user?.account_type,
          label: ACCTYPE_NAME_BY_ACCTYPE_VALUE[settings?.user?.account_type],
        } || { value: 0, label: ACCTYPE_NAME_BY_ACCTYPE_VALUE[0] },
        moderationType: {
          value: settings?.user?.moderation_policy_type,
          label: MODTYPE_NAME_BY_MODTYPE_VALUE[settings?.user?.moderation_policy_type],
        } || { value: 0, label: MODTYPE_NAME_BY_MODTYPE_VALUE[0] },
      })
    }
  }, [settings])

  const [token, setToken] = useState<string>('')
  const [tokenLoading, setTokenLoading] = useState(false)

  useEffect(() => {
    const getToken = async () => {
      setTokenLoading(true)
      try {
        const { data } = await settingsApi.getAPIToken()
        setToken(data?.token)
      } catch (e: any) {
        setAppError(e)
      } finally {
        setTokenLoading(false)
      }
    }
    getToken()
  }, [])

  const handleCreateToken = async () => {
    setTokenLoading(true)
    try {
      const { data } = await settingsApi.postAPIToken()
      setToken(data?.token)
    } catch (e: any) {
      setAppError(e)
    } finally {
      setTokenLoading(false)
    }
  }

  const [copied, setCopied] = useState(false)
  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value)
  }

  const subSince =
    settings?.subscription?.subs_start &&
    format(transformUTCToLocale(settings?.subscription?.subs_start), 'MMM dd yyyy HH:mm')
  const subUntil =
    settings?.subscription?.subs_end &&
    format(transformUTCToLocale(settings?.subscription?.subs_end), 'MMM dd yyyy HH:mm')

  const [submitLoading, setSubmitLoading] = useState<boolean>(false)

  const onSubmit = async (fData: IFormData) => {
    setSubmitLoading(true)
    try {
      const formData = new FormData()
      formData.append('avatar', fData?.avatar?.[0])
      formData.append(
        'data',
        JSON.stringify({
          name: fData.name,
          description: fData.description || null,
          account_type: fData.accountType.value,
          moderation_policy_type: fData.moderationType.value,
        })
      )
      await settingsApi.update(formData)
      await fetchSettings()
      setSuccessMessage('Setting applied successfully')
      return
    } catch (error: any) {
      setAppError(error)
    } finally {
      setSubmitLoading(false)
    }
  }

  return (
    <div data-testid="settings-page">
      <Header className={s.headerWrapper}>
        <PageTitle title="Settings" />
      </Header>
      <main>
        {loading || submitLoading ? (
          <div className={styles.loaderWrapper} data-testid="settings-loader">
            <Spinner />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className={s.settingsGrid} data-testid="settings-form">
            <div className={s.leftCol}>
              <div className={s.settingsGrid__item}>
                <div className={s.subBlock}>
                  <div>
                    <span className={s.label}>Your Subscription type</span>
                    <div
                      className={s.subType}
                      style={{ color: settings?.user?.user_role ? '#3888ff' : '#fb0943', marginTop: 8 }}
                    >
                      {settings?.user?.user_role ? (
                        <div className={s.userRoleWrapper}>
                          <BriefcaseIcon className={`${s.icon24} ${s.blue}`} /> Enterprise
                        </div>
                      ) : (
                        <div className={s.userRoleWrapper}>
                          <ClockIcon className={`${s.icon24} ${s.red}`} />
                          DEMO
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    {settings?.user?.user_role ? (
                      <InfoIcon className={s.infoIcon} />
                    ) : (
                      <>
                        <span className={`${s.label} ${s.withOpacity}`}>Showtime left</span>
                        <div className={`${s.subType} ${s.mt8}`}>{settings?.subscription?.rest_camp_time} min</div>
                      </>
                    )}
                  </div>
                </div>
                {settings?.user?.user_role ? (
                  <div className={s.subDates}>
                    <div>
                      <span className={`${s.label} ${s.withOpacity}`}>Since</span>
                      <div className={`${s.subType} ${s.sinceUntil}`}>{subSince}</div>
                    </div>
                    <div>
                      <span className={`${s.label} ${s.withOpacity}`}>Until</span>
                      <div className={`${s.subType} ${s.sinceUntil}`}>{subUntil}</div>
                    </div>
                  </div>
                ) : (
                  <CustomButton type="button" variant="primary" className={s.subHistoryButton} onClick={handleOpenSub}>
                    Upgrade to Enterprise
                  </CustomButton>
                )}
                <CustomButton
                  type="button"
                  className={s.subHistoryButton}
                  variant="outlined"
                  disabled={loading}
                  onClick={handleOpenSubHistory}
                >
                  Subscription history
                </CustomButton>
              </div>
              <div className={s.settingsGrid__item}>
                {!token && <span className={s.label}>API Token Key</span>}
                {!!token && (
                  <Input
                    label={
                      <div className={s.apiTokenLabelWrapper}>
                        <span>API Token Key</span>
                        <button
                          className={s.copyButton}
                          type="button"
                          onClick={() => {
                            copyToClipboard(token)
                            setCopied(true)
                          }}
                        >
                          {copied ? 'Copied' : ''}
                          <CopyIcon className={s.copyIcon} />
                        </button>
                      </div>
                    }
                    type="text"
                    data-testid="api-token-input"
                    placeholder="api_token: str"
                    value={token || ''}
                    readOnly
                    onClick={(e: any) => {
                      e.target.select()
                      copyToClipboard(token)
                      setCopied(true)
                    }}
                    icon={<LockIcon className={s.lockIcon} />}
                  />
                )}
                <CustomButton
                  variant="primary"
                  type="button"
                  onClick={handleCreateToken}
                  className={s.createTokenButton}
                  disabled={tokenLoading}
                  testid="createTokenBtn"
                >
                  {tokenLoading ? <Spinner small /> : 'Create New'}
                </CustomButton>
              </div>
              <div className={s.settingsGrid__item}>
                <SelectControlled
                  label="Campaigns moderation type"
                  icon={<PublisherIcon />}
                  control={control}
                  name="moderationType"
                  options={optionsAutoApproval}
                  placeholder="Moderation type"
                />
              </div>
              <div className={s.settingsGrid__item}>
                <SelectControlled
                  label="Select your account type"
                  icon={<DiamondIcon />}
                  name="accountType"
                  control={control}
                  options={optionsAccountType}
                  placeholder="Account type"
                />
              </div>
            </div>

            <div className={`${s.settingsGrid__item} ${s.formWrapper}`}>
              <CustomDropzone
                control={control}
                name="avatar"
                uploaded={settings?.user?.avatar_url}
                noMediaTitle="Add Network Avatar"
                error={errors?.avatar?.message}
                wrapperStyles={{ alignSelf: 'center', alignItems: 'center' }}
                dropzoneStyles={{ height: 128, width: 128, borderRadius: '50%', margin: '0 auto' }}
                iconWidth={40}
                iconHeight={40}
              />
              <InputControlled
                icon={<TagIcon />}
                control={control}
                name="name"
                placeholder="Ad Network name..."
                error={errors?.name?.message}
              />
              <TextareaControlled
                height={156}
                icon={<InfoIcon />}
                control={control}
                name="description"
                placeholder="Description..."
                error={errors?.description?.message}
              />
              <CustomButton type="submit" variant="primary" size="240" className={s.saveButton} testid="saveBtn">
                Save
              </CustomButton>
            </div>
          </form>
        )}
      </main>
      <SubscriptionHistory show={showSubHistory} onHide={handleCloseSubHustory} />
      <Subscription show={showSub} onHide={handleCloseSub} />
    </div>
  )
}

export default Settings
